import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import api from "api/api";
import endpoints from "api/endpoints";
import { ErrorMessage } from "common";

import StripeWrapper from "../stripe/StripeWrapper";

import CheckoutSectionCard from "./CheckoutSectionCard";
import PaymentForm from "./PaymentForm";
import SavedPaymentMethods from "./SavedPaymentMethods";

import { Button, Icon } from "@/design-system";

export default function StripeCheckout({
  stripeCustomerId,
  orderTotal,
  handlePaymentSuccess,
  handlePaymentError,
  buildOrderPayload,
  isProcessing,
  setIsProcessing,
  userInfo,
  billingAddress,
  selectedItems,
}) {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);

  // Load saved payment method from localStorage
  useEffect(() => {
    const savedPaymentMethodId = localStorage.getItem(
      "selectedPaymentMethodId"
    );

    if (savedPaymentMethodId && stripeCustomerId) {
      setSelectedPaymentMethodId(savedPaymentMethodId);

      // Fetch the payment method details for the saved ID
      const fetchSavedPaymentMethod = async () => {
        try {
          const token = await getAccessTokenSilently();
          const response = await api.get(
            endpoints.GET_PAYMENT_METHODS(stripeCustomerId),
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const paymentMethods = response.data.paymentMethods || [];
          const paymentMethod = paymentMethods.find(
            (pm) => pm.id === savedPaymentMethodId
          );
          if (paymentMethod) {
            setSelectedPaymentMethod(paymentMethod);
          } else {
            // Payment method not found, clear saved data
            localStorage.removeItem("selectedPaymentMethodId");
            setSelectedPaymentMethodId(null);
          }
        } catch (error) {
          // Clear invalid saved data on error
          localStorage.removeItem("selectedPaymentMethodId");
          setSelectedPaymentMethodId(null);
        }
      };

      fetchSavedPaymentMethod();
    }
  }, [stripeCustomerId, getAccessTokenSilently]);

  const handleSelectPaymentMethod = (paymentMethodId, paymentMethod = null) => {
    setSelectedPaymentMethodId(paymentMethodId);
    setSelectedPaymentMethod(paymentMethod);
    localStorage.setItem("selectedPaymentMethodId", paymentMethodId);
  };

  return (
    <StripeWrapper>
      <div className="flex flex-col gap-[var(--space-5)]">
        {/* Payment Method Selection */}
        <CheckoutSectionCard title="Complete your payment">
          <p className="mb-[var(--space-4)] mt-[-6px] text-sm text-ink-2">
            Confirm the card for this order.
          </p>
          <SavedPaymentMethods
            customerId={stripeCustomerId}
            onSelectPaymentMethod={handleSelectPaymentMethod}
            selectedPaymentMethodId={selectedPaymentMethodId}
            showTitle={false}
          />
        </CheckoutSectionCard>

        {error && <ErrorMessage message={error} />}

        {/* Payment Form */}
        {selectedPaymentMethod ? (
          <PaymentForm
            paymentMethod={selectedPaymentMethod}
            orderTotal={orderTotal}
            customerId={stripeCustomerId}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            buildOrderPayload={buildOrderPayload}
            isProcessing={isProcessing}
            setIsProcessing={setIsProcessing}
            userInfo={userInfo}
            billingAddress={billingAddress}
            selectedItems={selectedItems}
          />
        ) : (
          <div className="rounded-xl border border-line-subtle bg-card px-[var(--space-6)] py-[var(--space-8)] text-center shadow-sm">
            <span className="mb-[14px] inline-flex h-[54px] w-[54px] items-center justify-center rounded-full bg-soft text-blue-600">
              <Icon name="credit_card" size={28} />
            </span>
            <h3 className="mb-[var(--space-1)] text-md font-semibold">
              Select a payment method
            </h3>
            <p className="mx-auto mt-0 text-sm text-ink-2">
              Choose a card above to continue with your payment.
            </p>
          </div>
        )}

        {/* Navigation */}
        <div>
          <Button
            variant="outline"
            startIcon="arrow_back"
            onClick={() => navigate("/checkout/payment")}
          >
            Back to payment & shipping
          </Button>
        </div>
      </div>
    </StripeWrapper>
  );
}
